import { useFeatureFlag } from '@newfront-insurance/admin-ui';

import { ArchivedChatsSettings } from './archived-chats-settings';
import { CustomSkillsSettings } from './custom-skills-settings';
import { NotificationSettings } from './notification-settings';
import { NovaSchedulesSettings } from './nova-schedules-settings';
import { OutlookConnectSettings } from './outlook-connect-settings';
import { NOVA_SCHEDULES_FEATURE_FLAG } from '../../../lib/nova-schedule-proposal';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';

/**
 * Dedicated settings panel component for chat area.
 * Displays settings content inline within the main chat content area
 * instead of as a modal dialog overlay.
 *
 * Provides SettingsPanelContext so any settings card can push a full-panel
 * sub-view (e.g. the Outlook manage view) without this component needing to
 * know what's inside it.
 */
export function ChatSettingsPanel({ showOutlookConnect = true }: { showOutlookConnect?: boolean }) {
  const { settingsSection } = useChatOverlayContext();
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const areNovaSchedulesEnabled = useFeatureFlag(NOVA_SCHEDULES_FEATURE_FLAG, false);
  const activeSettingsSection =
    settingsSection === 'skills' && isNovaSkillsEnabled
      ? 'skills'
      : settingsSection === 'scheduled' && areNovaSchedulesEnabled
        ? 'scheduled'
        : 'general';

  return (
    <div className="flex size-full min-h-0 min-w-0 flex-col">
      <div
        className={`flex min-h-0 flex-1 items-start justify-center overflow-y-auto [scrollbar-gutter:stable_both-edges] ${
          activeSettingsSection === 'skills' || activeSettingsSection === 'scheduled' ? 'px-8 pt-10 pb-8' : 'p-6'
        }`}
      >
        <div className="flex w-[736px] max-w-full shrink-0 flex-col gap-4">
          {activeSettingsSection === 'skills' ? (
            <CustomSkillsSettings layout="page" />
          ) : activeSettingsSection === 'scheduled' ? (
            <NovaSchedulesSettings />
          ) : (
            <div className="flex w-full max-w-md flex-col gap-4 self-center">
              <NotificationSettings />
              {showOutlookConnect ? <OutlookConnectSettings /> : null}
              <ArchivedChatsSettings />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
